/**
 * Insights Workshop client: the types the ported Collaboration screens use,
 * and their calls pointed at /api/v1/workshop — never the Insights Lab's
 * /api/v1/playground. Types are copied (not imported) from the Lab's
 * lib/projects.ts at the commit this port was taken from; the Workshop
 * shares no code with the Lab (app/workshop/__init__.py).
 *
 * The ported screens speak (game_code, org_id); the Workshop API speaks
 * games.id. registerGame() records the pair when the game list loads.
 */
import { trackEvent } from '../../lib/analytics'
import { api } from '../../lib/api'

const gameIds = new Map<string, number>()
const gameKey = (orgId: number | string | null | undefined, code: string) => `${orgId}:${code}`

/** Remember which games.id a (club, game code) pair is. */
export function registerGame(orgId: number, gameCode: string, gameId: number): void {
  gameIds.set(gameKey(orgId, gameCode), gameId)
}

export function gameIdFor(orgId: number | string | null | undefined, gameCode: string): number {
  const id = gameIds.get(gameKey(orgId, gameCode))
  if (id == null) throw new Error('Pick a game from the Analysis Room list first.')
  return id
}




export interface VisualSuggestion {
  label: string
  question: string
}

export interface CanvasCard {
  id: string
  /** 'query' = NL/SQL result card, 'note' = free-text note. */
  kind: 'query' | 'note'
  question: string
  sql: string
  explanation: string
  /** 'local' = pattern engine, 'llm' = Claude, '' = manual/note. */
  source: string
  result: QueryResult | null
  error: string | null
  visual: VisualKind
  mapping: Record<string, string | undefined>
  /** Free-text note content (for note cards). */
  note: string
  /** Performance diagnostics from the NL query pipeline. */
  perf: QueryPerf | null
  /** The pitch region the data was filtered to (e.g. 'attacking third'),
   *  so the chart can zoom to that slice. */
  region: string
  /** Companion visualizations offered as one-click new cards. */
  suggestions: VisualSuggestion[]
  position: { x: number; y: number }
  size: { w: number; h: number }
}

export type VisualKind =
  | 'table' | 'bar' | 'line' | 'scatter' | 'pitch'
  | 'heatmap' | 'flow' | 'histogram' | 'pitchzones'
  | 'shape' | 'spacemap' | 'timeline' | 'passnet' | 'rose'

export type SpatialRole = 'x' | 'y' | 'x2' | 'y2' | 'team' | 'player' | 'frame' | 'size'

export interface SpatialContract {
  roles: Partial<Record<SpatialRole, string>>
  frame: {
    origin: 'center' | 'corner'; units: 'm' | 'cm'; units_divisor: number
    view?: 'canonical' | 'attacking' | 'camera'
  }
  plottable: VisualKind[]
  source: 'declared' | 'inferred'
  detail: string
}

export interface QueryResult {
  columns: { name: string; type: string }[]
  rows: unknown[][]
  row_count: number
  truncated: boolean
  elapsed_ms: number
  tables: string[]
  spatial?: SpatialContract | null
}

export interface PitchLayer {
  id: string
  /** The question that produced it — the legend label. */
  label: string
  /** How to draw it: the visual kind of the card it came from. */
  kind: VisualKind
  color: string
  visible: boolean
  columns: { name: string; type: string }[]
  rows: unknown[][]
  mapping: Record<string, string | undefined>
  /** The pitch region this data was filtered to (e.g. 'attacking third'),
   *  so the board zooms to that slice instead of a full pitch. */
  region?: string
}

export interface CanvasDraft {
  cards: CanvasCard[]
  /** Layers on the always-visible pitch column. Absent in older drafts. */
  pitchLayers?: PitchLayer[]
  [key: string]: unknown
}

// ── Checklist ────────────────────────────────────────────────────────────────

export interface ChecklistItem {
  id: string
  text: string
  done: boolean
  /** Nesting depth: 0 = top-level, 1 = child, 2 = grandchild. */
  depth: number
}

// ── NL query ────────────────────────────────────────────────────────────────

export interface QueryPerf {
  total_ms: number
  meta_ms: number
  translate_ms: number
  query_ms: number
  row_count?: number
  tables_scanned?: string[]
  warnings: string[]
  plan: string
}

export interface QueryPerspective {
  viewing: 'ours' | 'theirs'
  resolved: boolean
  reason?: string
  us_team_id?: number
  opp_team_id?: number
  home_team_id?: number
  us_name?: string
  opp_name?: string
}

export interface NLQueryResponse {
  question: string
  sql: string
  explanation: string
  source: string
  /** The engine's suggested visualization for this result ('' = table). */
  visual?: string
  /** The pitch region the data was filtered to (e.g. 'attacking third'),
   *  so the chart can clip/zoom to that slice instead of drawing the full
   *  pitch with a few scattered dots. */
  region?: string
  /** Companion visualizations worth adding as their own cards. */
  suggestions?: VisualSuggestion[]
  perspective?: QueryPerspective
  result: QueryResult | null
  error?: string
  perf?: QueryPerf
}

/** A Workshop project as the ported canvas reads it. */
export interface AnalyticsProject {
  id: number
  owner_id: number
  title: string
  org_id: number | null
  game_code: string | null
  game_id?: number | null
  tab: string | null
  draft: Record<string, unknown> | null
  is_default: boolean
  shared: boolean
  can_edit?: boolean
  owner_name?: string | null
  created_at: string | null
  updated_at: string | null
}

type ServerProject = Omit<AnalyticsProject, 'game_code' | 'tab' | 'is_default' | 'shared'>

function toProject(p: ServerProject, gameCode: string | null = null): AnalyticsProject {
  return { ...p, game_code: gameCode, tab: null, is_default: false, shared: true }
}

export async function listProjects(orgId: number, gameId?: number): Promise<AnalyticsProject[]> {
  const qs = `org_id=${orgId}${gameId ? `&game_id=${gameId}` : ''}`
  const r = await api<{ projects: ServerProject[] }>(`/api/v1/workshop/projects?${qs}`)
  return r.projects.map((p) => toProject(p))
}

export async function createProject(data: {
  title: string; org_id: number; game_id?: number | null; draft?: Record<string, unknown> | null
}): Promise<AnalyticsProject> {
  const r = await api<{ project: ServerProject }>('/api/v1/workshop/projects', {
    method: 'POST', body: JSON.stringify(data),
  })
  return toProject(r.project)
}

export async function updateProject(id: number, data: {
  title?: string; draft?: Record<string, unknown> | null
}): Promise<{ project: AnalyticsProject }> {
  const r = await api<{ project: ServerProject }>(`/api/v1/workshop/projects/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  })
  return { project: toProject(r.project) }
}

export async function duplicateProject(id: number, title?: string): Promise<AnalyticsProject> {
  const r = await api<{ project: ServerProject }>(`/api/v1/workshop/projects/${id}/duplicate`, {
    method: 'POST', body: JSON.stringify(title ? { title } : {}),
  })
  return toProject(r.project)
}

export function deleteProject(id: number): Promise<unknown> {
  return api(`/api/v1/workshop/projects/${id}`, { method: 'DELETE' })
}

/** Ask AI writes the SQL and the server runs it (run: true). Only table
 *  and column names reach the model — never results (app/workshop/ask.py). */
export async function nlQuery(data: {
  question: string; game_code: string; org_id: number; limit?: number
  tables?: unknown[]; perspective?: 'ours' | 'theirs'
}): Promise<NLQueryResponse> {
  const gid = gameIdFor(data.org_id, data.game_code)
  // Every Ask AI surface rides through here — the one place the event
  // cannot be forgotten. The question itself is customer content and
  // stays out of the properties.
  trackEvent('Workshop Ask Sent', { game_code: data.game_code })
  const r = await api<{ reply: string; sql: string | null; explanation: string | null
                        visual: string; result: QueryResult | null; error: string | null }>(
    `/api/v1/workshop/games/${gid}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question: data.question, run: true, limit: data.limit }),
    })
  return {
    question: data.question, sql: r.sql ?? '', explanation: r.explanation ?? r.reply ?? '',
    source: 'llm', visual: r.visual, result: r.result,
    error: r.error ?? (r.sql ? undefined : (r.reply || 'Ask AI did not write a query.')),
  }
}

export function runQuery(data: {
  game_code: string; org_id: number; sql: string; limit?: number
}): Promise<QueryResult> {
  const gid = gameIdFor(data.org_id, data.game_code)
  // As nlQuery: the chokepoint every canvas/table query rides through.
  // The SQL stays out of the properties (customer content).
  trackEvent('Workshop Query Run', { game_code: data.game_code })
  return api<QueryResult>(`/api/v1/workshop/games/${gid}/query`, {
    method: 'POST', body: JSON.stringify({ sql: data.sql, limit: data.limit }),
  })
}

export function framePositions<T>(gameCode: string, orgId: number, frames: number[]): Promise<T> {
  const gid = gameIdFor(orgId, gameCode)
  return api<T>(`/api/v1/workshop/games/${gid}/frame-positions`, {
    method: 'POST', body: JSON.stringify({ frames }),
  })
}
